import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput, FormFieldTextarea } from '@/shared/components/custom/form-field';
import { useToast } from '@/shared/hooks/useToast';
import { useBoardStatuses } from '@/features/boards/public';
import { useProjectMembers } from '@/features/projects/public';
import { useCreateBoardTask } from '@/features/tasks/public';
import { quickTaskDraftSchema, quickTaskDraftDefaultValues } from '../schemas';
import type { QuickTaskDraftFormValues } from '../schemas';
import { BlankTaskChips } from './BlankTaskChips';
import type { TaskFieldOptions } from './TaskFieldEditor';

export interface BlankTaskPanelProps {
  projectId: string;
  boardId: string;
  /** Closes the popover: after a successful create, or on cancel with an empty form. */
  onClose: () => void;
}

/**
 * The *Agregar tarea* tab: title, description and the three fields worth setting before a
 * task exists — priority, due date and assignee.
 *
 * Nothing here is AI: it shares `quickTaskDraftSchema` and the chip editors with the AI tab
 * because both tabs produce the same task, so the fields they have in common validate and
 * are edited the same way — not because the blank form interprets anything.
 *
 * The task lands on the board's first column, since the create endpoint requires a statusId
 * and the tab asks for no status. Cancel reads as one escape key pressed twice: with
 * anything typed it clears the form, and on an untouched one it closes the popover.
 */
export function BlankTaskPanel({ projectId, boardId, onClose }: BlankTaskPanelProps) {
  const { t } = useTranslation('ai');
  const { toast } = useToast();
  const {
    statuses,
    isLoading: statusesLoading,
    isError: statusesError,
  } = useBoardStatuses(boardId);
  const members = useProjectMembers(projectId).data ?? [];
  const createTask = useCreateBoardTask(projectId, boardId);

  // Members only: the status is decided below and the form offers no tag, so building the
  // AI tab's whole board context here would fetch two lists nothing reads.
  const options: TaskFieldOptions = {
    statuses: [],
    groups: [],
    tags: [],
    members: members.map((member) => ({
      id: member.userId,
      label: member.displayName ?? member.email,
    })),
  };

  const form = useForm<QuickTaskDraftFormValues>({
    resolver: zodResolver(quickTaskDraftSchema),
    defaultValues: quickTaskDraftDefaultValues,
  });
  // Only the fields this panel reads. `watch()` with no argument subscribes to every one, so a
  // keystroke in the title re-rendered all three chips and their editors.
  const values = form.watch(['title', 'priority', 'dueDate', 'assigneeId']);
  const [title, priority, dueDate, assigneeId] = values;
  const chipValues = { title, priority, dueDate, assigneeId } as Partial<QuickTaskDraftFormValues>;

  // ponytail: the board itself groups columns by type zone before `order`, so a board whose
  // `order` runs against its zones could show a different leftmost column. Reuse the board's
  // own sort here if that ever shows up.
  const firstStatusId = [...statuses].sort((a, b) => a.order - b.order)[0]?.id;
  const isEmpty = !form.formState.isDirty;

  function handleCreate(draft: QuickTaskDraftFormValues) {
    if (!firstStatusId || createTask.isPending) return;
    createTask.mutate(
      {
        title: draft.title.trim(),
        statusId: firstStatusId,
        description: draft.description ?? undefined,
        priority: draft.priority ?? undefined,
        assigneeId: draft.assigneeId ?? undefined,
        dueDate: draft.dueDate ? { date: draft.dueDate } : null,
      },
      {
        onSuccess: () => {
          form.reset(quickTaskDraftDefaultValues);
          toast.success({ title: t('quickCreate.toasts.taskCreated') });
          onClose();
        },
        onError: () => {
          toast.error({ title: t('quickCreate.blank.createError') });
        },
      },
    );
  }

  return (
    <form onSubmit={form.handleSubmit(handleCreate)}>
      <div className="space-y-3 p-4">
        {/* Without a column the task has nowhere to land, so Accept can never enable. Saying so
            beats a button that is simply dead — the failure this tab was built to stop. */}
        {!statusesLoading && !firstStatusId && (
          <p role="alert" data-testid="blank-task-unavailable" className="text-xs text-destructive">
            {t(statusesError ? 'quickCreate.blank.statusesFailed' : 'quickCreate.blank.noStatuses')}
          </p>
        )}
        <FormFieldInput
          control={form.control}
          name="title"
          hideLabel
          label={t('quickCreate.blank.titleLabel')}
          placeholder={t('quickCreate.blank.titlePlaceholder')}
          disabled={statusesLoading}
          data-testid="blank-task-title"
        />
        <FormFieldTextarea
          control={form.control}
          name="description"
          hideLabel
          rows={2}
          label={t('quickCreate.blank.descriptionLabel')}
          placeholder={t('quickCreate.blank.descriptionPlaceholder')}
          disabled={statusesLoading}
          data-testid="blank-task-description"
        />
        <BlankTaskChips
          control={form.control}
          values={chipValues}
          options={options}
          projectId={projectId}
          boardId={boardId}
        />
      </div>

      {/* Same footer geometry as the AI tab so switching tabs does not move the buttons. */}
      <div className="flex justify-end gap-2.5 border-t px-4 py-3.5">
        <Button
          type="button"
          variant="outline"
          data-testid="blank-task-cancel"
          onClick={() => {
            if (isEmpty) onClose();
            else form.reset(quickTaskDraftDefaultValues);
          }}
        >
          {t('quickCreate.cancel')}
        </Button>
        <Button
          type="submit"
          data-testid="blank-task-confirm"
          disabled={!title?.trim() || !firstStatusId || createTask.isPending}
        >
          {createTask.isPending ? t('quickCreate.confirmPending') : t('quickCreate.blank.accept')}
        </Button>
      </div>
    </form>
  );
}
